import { DragIcon } from '@mm/icons';
import { ReactComponent as FancyTrashIcon } from 'assets/garbage.svg';
import { ReactComponent as PathIcon } from 'assets/paths-icon.svg';
import { ReactComponent as TopWidgetArrow } from 'assets/top-widget-arrow.svg';
import classnames from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { generatePageStructure } from '../../../../../common/components/FilterableSelect/filterable-select-helpers';
import { InputWithEmoji } from '../../../../../common/components/InputWithEmoji/InputWithEmoji';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { useButtonHandlers } from '../../../../../common/helpers/ice-breaker-helpers/use-button-handlers';
import { ActivePathType } from '../../../../../common/widgets/common/ActivePathSelection/ActivePathSelection';
import { PathGraphContext } from '../../../../../common/widgets/common/PathGraph/PathGraph';
import {
  GenericButtonModel,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import { IceBreakerModel } from '@mm/state/model/IceBreakerModel';
import TagModel from '@mm/state/model/TagModel';
import { StoreState } from '@mm/state/store-interfaces';
import { colors } from '../../../beta/styles/colors';
import { ButtonTagsForm } from '../../../build/components/widgets/buttons/ButtonConfigform/ButtonTagsForm';
import './IceBreakerButton.pcss';

type Props = {
  iceBreaker: IceBreakerModel;
  button: GenericButtonModel<PageButtonConfig>;
  openedButtonId: number | undefined;
  setOpenedButtonId: (openedButtonId: number | undefined) => void;
  setActivePathType: (activePathType: ActivePathType) => void;
  isBotBuilderLayout?: boolean;
  deleteable: boolean;
  setActivePageId: (id: number) => void;
};

export const IceBreakerButton = (props: Props) => {
  const {
    button,
    iceBreaker,
    setOpenedButtonId,
    openedButtonId,
    setActivePathType,
    deleteable,
    setActivePageId,
  } = props;

  const buttonHandlers = useButtonHandlers();

  const [buttonName, setButtonName] = useState('');
  const { config } = button;
  const pageId = config.pageId;
  const [tags, setTags] = useState<TagModel[]>(button.tags || []);
  const [selectedPageId, setSelectedPageId] = useState(pageId);
  const pages = useSelector((state: StoreState) => state.page.entities);
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );
  const noOption = { name: 'No', id: -1, pageGroupId: -1, kind: null as any };
  const selectedPage = pages.find(p => p.id === selectedPageId) || noOption;
  const selectorPages = [...pages, noOption];
  const organizedPageOptions = useMemo(
    () => generatePageStructure(selectorPages, pageGroups, true),
    [selectorPages, pageGroups],
  );

  const { setActiveNode, deselectWidgetButtons } = useContext(PathGraphContext);

  useEffect(() => {
    setSelectedPageId(button.config.pageId);
    setButtonName(button.name);
    setTags(button.tags || []);
  }, [button, openedButtonId]);

  const onClose = () => {
    setOpenedButtonId(undefined);
    deselectWidgetButtons?.(iceBreaker.buttons);
  };

  const openButton = () => {
    deselectWidgetButtons?.(iceBreaker.buttons);
    setOpenedButtonId(button.id);
    setActivePageId(button.config.pageId);
    setActiveNode?.({
      id: button.id,
      type: 'button',
      dialogueId: button.config.pageId,
      action: 'open',
    });
    setActivePathType('facebook');
  };

  const saveButton = () => {
    if (selectedPageId === -1) {
      return;
    }
    const updatedButton: GenericButtonModel<PageButtonConfig> = {
      ...button,
      name: buttonName,
      config: { ...button.config, pageId: selectedPageId },
      tags,
    };
    buttonHandlers.updateButton(iceBreaker, updatedButton);
    onClose();
  };

  const deleteButton = () => {
    buttonHandlers.delete(
      iceBreaker.botId,
      iceBreaker.id,
      iceBreaker.kind,
      button,
    );
    onClose();
  };

  const onSelectPage = ({ id }: { id: number }) => {
    setSelectedPageId(id);
    setActivePageId(id);
    setActiveNode?.({
      id: button.id,
      type: 'button',
      dialogueId: id,
      action: 'open',
    });
  };

  const saveDisabled = selectedPageId === -1 || !buttonName.trim();
  const opened = openedButtonId === button.id;

  return (
    <div className="ice-breaker-button">
      {opened && (
        <div className="ice-breaker-button__edit-area">
          <div className="ice-breaker-button__edit-area-bubble">
            <div className="ice-breaker-button__input-label">
              <span>Icebreaker Question</span>
            </div>
            <InputWithEmoji
              className="ice-breaker-button__text-input-with-emoji-picker"
              textInputClassName="ice-breaker-button__text-input"
              value={buttonName}
              onChange={setButtonName}
            />
            <div className="ice-breaker-button__associated-dialogue">
              <div className="ice-breaker-button__input-label">
                <PathIcon className="ice-breaker-button__path-icon ice-breaker-button__path-icon-label" />
                <span>Respond with the following dialogue</span>
              </div>

              <div className="ice-breaker-button__associated-dialogue-input-wrapper">
                <PrimarySelect
                  showSearchFilter={true}
                  placeholder="No"
                  className="ice-breaker-button__associated-dialogue-input"
                  onChange={onSelectPage}
                  organizedOptions={organizedPageOptions}
                  value={selectedPage}
                />
              </div>
            </div>
            <div className="ice-breaker-button__tags-wrapper">
              <ButtonTagsForm
                title="Add a Tag to contacts who click this button"
                kind="add_tags"
                tags={tags}
                onTagAdd={(tag: TagModel) =>
                  setTags(Array.from(new Set([tag].concat(tags))))
                }
                onTagRemove={(tag: TagModel) =>
                  setTags(tags.filter(t => t.name !== tag.name))
                }
              />
            </div>
            <div className="ice-breaker-button__edit-area-buttons">
              <div className="ice-breaker-button__edit-area-button-left">
                {deleteable && (
                  <span
                    className="ice-breaker-button__delete-button"
                    onClick={deleteButton}
                  >
                    <FancyTrashIcon className="ice-breaker-button__delete-icon" />
                  </span>
                )}
              </div>
              <div className="ice-breaker-button__edit-area-button-right">
                <span
                  className="ice-breaker-button__cancel-button"
                  onClick={onClose}
                >
                  Cancel
                </span>
                <PrimaryButton
                  size="lg"
                  className={classnames('ice-breaker-button__edit-button', {
                    'ice-breaker-button__edit-button--disabled': saveDisabled,
                  })}
                  disabled={saveDisabled}
                  onClick={saveButton}
                >
                  Save
                </PrimaryButton>
              </div>
            </div>
          </div>
          <TopWidgetArrow className="ice-breaker-button__edit-area-bottom-arrow" />
        </div>
      )}

      <div
        onClick={opened ? onClose : openButton}
        className={classnames('ice-breaker-button__button', {
          'ice-breaker-button__button--selected': opened,
        })}
      >
        <div className="ice-breaker-button__icon-container ice-breaker-button__icon-container--left">
          <DragIcon
            color={opened ? '#fff' : colors.oldBlue}
            className="ice-breaker-button__drag-icon"
          />
        </div>
        <span className="ice-breaker-button__button-text">{button.name}</span>

        <div className="ice-breaker-button__icon-container">
          <PathIcon className="ice-breaker-button__path-icon" />
        </div>
      </div>
    </div>
  );
};
